import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  HOME_PERIOD_CUSTOM_MAX_DAYS,
  homePeriodDayCount,
  homePeriodPresetButtonLabel,
  isYmd,
  type HomePeriodPreset,
  type HomePeriodState,
} from "./home-metric-period";

const CARD_PRESETS = ["7", "30", "mtd"] as const;

/** Quiet 7d · 30d · MTD on the card — plan/50. */
export function HomePeriodPresetToggle(props: {
  state: HomePeriodState;
  onPreset: (preset: Exclude<HomePeriodPreset, "custom">) => void;
  onCustom: () => void;
}) {
  return (
    <div
      className="inline-flex flex-wrap items-center gap-0.5 text-xs tabular-nums"
      role="group"
      aria-label="Period"
    >
      {CARD_PRESETS.map((p) => {
        const active = props.state.preset === p;
        return (
          <button
            key={p}
            type="button"
            aria-pressed={active}
            className={
              active
                ? "rounded px-1.5 py-0.5 font-medium text-[var(--color-foreground)]"
                : "rounded px-1.5 py-0.5 text-[var(--color-muted)] hover:text-[var(--color-foreground)]"
            }
            onClick={() => props.onPreset(p)}
          >
            {homePeriodPresetButtonLabel(p)}
          </button>
        );
      })}
      <button
        type="button"
        aria-pressed={props.state.preset === "custom"}
        className={
          props.state.preset === "custom"
            ? "rounded px-1.5 py-0.5 font-medium text-[var(--color-foreground)]"
            : "rounded px-1.5 py-0.5 text-[var(--color-muted)] hover:text-[var(--color-foreground)]"
        }
        onClick={props.onCustom}
      >
        Custom
      </button>
    </div>
  );
}

/** From → to form inside Show more — plan/50. */
export function HomePeriodCustomForm(props: {
  from: string;
  to: string;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
  onApply: () => void;
  pending?: boolean;
  error?: string | null;
  /** Unique id prefix when multiple period dialogs mount. */
  idPrefix?: string;
}) {
  const days = homePeriodDayCount(props.from, props.to);
  const valid =
    isYmd(props.from) &&
    isYmd(props.to) &&
    props.from <= props.to &&
    days > 0 &&
    days <= HOME_PERIOD_CUSTOM_MAX_DAYS;
  const fromId = `${props.idPrefix ?? "home-period"}-from`;
  const toId = `${props.idPrefix ?? "home-period"}-to`;

  return (
    <div className="mb-4 space-y-3 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-muted)]/20 p-3">
      <p className="m-0 text-xs text-[var(--color-muted)]">
        Custom range (company timezone). Max {HOME_PERIOD_CUSTOM_MAX_DAYS} days.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label htmlFor={fromId} className="text-xs">
            From
          </Label>
          <Input
            id={fromId}
            type="date"
            value={props.from}
            onChange={(e) => props.onFrom(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={toId} className="text-xs">
            To
          </Label>
          <Input
            id={toId}
            type="date"
            value={props.to}
            onChange={(e) => props.onTo(e.target.value)}
          />
        </div>
      </div>
      {props.error ? (
        <p className="m-0 text-xs text-[var(--color-destructive)]">{props.error}</p>
      ) : null}
      <Button
        type="button"
        size="sm"
        pending={props.pending}
        disabled={!valid || props.pending}
        onClick={props.onApply}
      >
        Apply range
      </Button>
    </div>
  );
}
